import moment from 'moment-timezone';
import type { FilterFn } from '@tanstack/react-table';
import type { CompoundFilterValue, AdvancedFilterRule } from '../types';

function evaluateDateRule(
  rawValue: unknown,
  rule: AdvancedFilterRule,
  parseDateFn: (rawValue: unknown) => moment.Moment | null,
  timezone: string,
): boolean {
  const date = parseDateFn(rawValue);
  if (!date || !date.isValid()) return false;

  switch (rule.operator) {
    case 'dateRange': {
      const from = rule.params.from as string;
      const to = rule.params.to as string;
      if (!from && !to) return true;
      if (from) {
        const fromMoment = moment.tz(from, 'YYYY-MM-DD', timezone).startOf('day');
        if (date.isBefore(fromMoment)) return false;
      }
      if (to) {
        const toMoment = moment.tz(to, 'YYYY-MM-DD', timezone).endOf('day');
        if (date.isAfter(toMoment)) return false;
      }
      return true;
    }
    case 'dateYear': {
      const year = rule.params.year as number;
      // Get the year in the specified timezone
      return date.tz(timezone).year() === year;
    }
    case 'dateBefore': {
      const before = rule.params.date as string;
      if (!before) return true;
      const beforeMoment = moment.tz(before, 'YYYY-MM-DD', timezone).startOf('day');
      return date.isBefore(beforeMoment);
    }
    case 'dateAfter': {
      const after = rule.params.date as string;
      if (!after) return true;
      const afterMoment = moment.tz(after, 'YYYY-MM-DD', timezone).endOf('day');
      return date.isAfter(afterMoment);
    }
    default:
      return true;
  }
}

/**
 * Creates a compound FilterFn that handles both value-selection and advanced filter rules.
 * The displayFn, parseDateFn, and timezone are captured in the closure so they're available at filter time.
 */
export function createCompoundFilter<TData>(
  displayFn: ((rawValue: unknown) => string) | undefined,
  parseDateFn: ((rawValue: unknown) => moment.Moment | null) | undefined,
  timezone: string,
): FilterFn<TData> {
  return ((row, columnId, filterValue: CompoundFilterValue | string[]) => {
    const rawValue = row.getValue(columnId);

    // Backward compatibility: if filterValue is a plain string array, treat as value-selection only
    if (Array.isArray(filterValue)) {
      if (filterValue.length === 0) return false;
      const displayValue = rawValue == null || String(rawValue).trim() === ''
        ? '(Blank)'
        : displayFn ? displayFn(rawValue) : String(rawValue);
      return filterValue.includes(displayValue);
    }

    // CompoundFilterValue
    const { selectedValues, advancedRules } = filterValue;

    // Evaluate value-selection (if present)
    let passesValueSelection = true;
    if (selectedValues !== undefined) {
      if (selectedValues.length === 0) return false;
      const displayValue = rawValue == null || String(rawValue).trim() === ''
        ? '(Blank)'
        : displayFn ? displayFn(rawValue) : String(rawValue);
      passesValueSelection = selectedValues.includes(displayValue);
    }

    // Evaluate advanced rules (all must pass — AND logic)
    let passesAdvancedRules = true;
    if (advancedRules && advancedRules.length > 0 && parseDateFn) {
      passesAdvancedRules = advancedRules.every(rule =>
        evaluateDateRule(rawValue, rule, parseDateFn, timezone)
      );
    }

    return passesValueSelection && passesAdvancedRules;
  }) as FilterFn<TData>;
}
